import React, { createContext, useContext, useState, useEffect, useMemo, useSyncExternalStore } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Themes whose `background` is a dark shade. Exposed as `isDark` so pages
// built on translucent overlays (e.g. bg-white/5 "glass" surfaces, which are
// invisible on a light ground) can pick an appropriate treatment per theme.
const DARK_THEMES = new Set(['dark', 'darkGreen', 'darkBlue', 'darkPurple', 'darkOrange']);

const themes = {
  light: {
    name: 'Light',
    background: 'bg-white',
    backgroundSecondary: 'bg-gray-50',
    text: 'text-gray-900',
    textSecondary: 'text-gray-600',
    textMuted: 'text-gray-500',
    link: 'text-blue-600',
    linkHover: 'hover:text-blue-800',
    primary: 'bg-blue-600 text-white',
    primaryHover: 'hover:bg-blue-700',
    secondary: 'bg-gray-200 text-gray-900',
    secondaryHover: 'hover:bg-gray-300',
    accent: 'bg-green-500 text-white',
    accentHover: 'hover:bg-green-600',
    border: 'border-gray-200',
    borderSecondary: 'border-gray-300',
    card: 'border-2 bg-white border-gray-300',
    cardHover: 'hover:bg-gray-50',
    input: 'bg-white border-gray-300 text-gray-900',
    shadow: 'shadow-md',
    gradient: 'bg-gradient-to-r from-blue-500 to-purple-600',
    // Button styling
    buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700',
    buttonSecondary: 'border border-blue-500 text-blue-600 hover:bg-blue-50',
    buttonDanger: 'bg-pink-500 text-white hover:bg-pink-600',
    buttonGhost: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    // Tab styling
    tabActive: 'bg-blue-600 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-blue-600',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-blue-600',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-blue-600',
    tabInactive: 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-gray-100',
      headerText: 'text-gray-700',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-white',
      oddRowBg: 'bg-gray-50',
      rowHover: 'hover:bg-gray-100',
      border: 'border-gray-200',
      textColor: 'text-gray-900',
      textSecondary: 'text-gray-600',
      winsColor: 'text-green-600',
      lossesColor: 'text-red-600',
      drawsColor: 'text-gray-500'
    }
  },
  dark: {
    name: 'Dark',
    background: 'bg-gray-900',
    backgroundSecondary: 'bg-gray-800',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    textMuted: 'text-gray-400',
    link: 'text-blue-400',
    linkHover: 'hover:text-blue-300',
    primary: 'bg-blue-500 text-white',
    primaryHover: 'hover:bg-blue-600',
    secondary: 'bg-gray-700 text-white',
    secondaryHover: 'hover:bg-gray-600',
    accent: 'bg-green-400 text-gray-900',
    accentHover: 'hover:bg-green-300',
    // One step lighter than `card`, deliberately. This was the card's own
    // background colour, so every bordered element sitting on a card had an
    // invisible border — buttons looked unbounded and checkboxes looked
    // absent until clicked. A border must differ from the surface it is on.
    border: 'border-gray-600',
    borderSecondary: 'border-gray-600',
    // One shade lighter than backgroundSecondary (bg-gray-800) — every
    // light-style theme already keeps `card` visually distinct from
    // `backgroundSecondary` (there, card is bg-white, backgroundSecondary is
    // a tint); the dark themes originally reused the identical shade for
    // both, so a card sitting on a backgroundSecondary surface (e.g. a
    // bracket Match card inside its Region panel) had zero contrast.
    card: 'border-2 bg-gray-700 border-gray-500',
    cardHover: 'hover:bg-gray-750',
    input: 'bg-gray-700 border-gray-600 text-white',
    shadow: 'shadow-xl',
    gradient: 'bg-gradient-to-r from-blue-600 to-purple-700',
    // Button styling
    buttonPrimary: 'bg-blue-500 text-white hover:bg-blue-600',
    buttonSecondary: 'border border-blue-400 text-blue-400 hover:bg-blue-900',
    buttonDanger: 'bg-pink-600 text-white hover:bg-pink-700',
    buttonGhost: 'bg-gray-700 text-gray-200 hover:bg-gray-600',
    // Tab styling
    tabActive: 'bg-blue-500 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-blue-500',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-blue-500',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-blue-500',
    tabInactive: 'text-gray-400 hover:bg-gray-700 hover:text-gray-100',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-gray-700',
      headerText: 'text-gray-100',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-gray-900',
      oddRowBg: 'bg-gray-800',
      rowHover: 'hover:bg-gray-750',
      border: 'border-gray-700',
      textColor: 'text-gray-100',
      textSecondary: 'text-gray-400',
      winsColor: 'text-green-400',
      lossesColor: 'text-red-400',
      drawsColor: 'text-gray-400'
    }
  },
  sports: {
    name: 'Sports',
    background: 'bg-green-50',
    backgroundSecondary: 'bg-green-100',
    text: 'text-green-900',
    textSecondary: 'text-green-700',
    textMuted: 'text-green-600',
    link: 'text-green-600',
    linkHover: 'hover:text-green-800',
    primary: 'bg-green-600 text-white',
    primaryHover: 'hover:bg-green-700',
    secondary: 'bg-orange-500 text-white',
    secondaryHover: 'hover:bg-orange-600',
    accent: 'bg-red-500 text-white',
    accentHover: 'hover:bg-red-600',
    border: 'border-green-200',
    borderSecondary: 'border-green-300',
    card: 'border-2 bg-white border-green-300',
    cardHover: 'hover:bg-green-50',
    input: 'bg-white border-green-300 text-green-900',
    shadow: 'shadow-lg',
    gradient: 'bg-gradient-to-r from-green-500 to-orange-500',
    // Button styling
    buttonPrimary: 'bg-green-600 text-white hover:bg-green-700',
    buttonSecondary: 'border border-green-600 text-green-600 hover:bg-green-50',
    buttonDanger: 'bg-pink-500 text-white hover:bg-pink-600',
    buttonGhost: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    // Tab styling
    tabActive: 'bg-green-600 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-green-600',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-green-600',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-green-600',
    tabInactive: 'text-green-700 hover:bg-green-100 hover:text-green-900',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-green-100',
      headerText: 'text-green-800',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-white',
      oddRowBg: 'bg-green-50',
      rowHover: 'hover:bg-green-100',
      border: 'border-green-200',
      textColor: 'text-green-900',
      textSecondary: 'text-green-700',
      winsColor: 'text-green-600',
      lossesColor: 'text-red-600',
      drawsColor: 'text-green-600'
    }
  },
  ocean: {
    name: 'Ocean',
    background: 'bg-cyan-50',
    backgroundSecondary: 'bg-cyan-100',
    text: 'text-cyan-900',
    textSecondary: 'text-cyan-700',
    textMuted: 'text-cyan-600',
    link: 'text-cyan-600',
    linkHover: 'hover:text-cyan-800',
    primary: 'bg-cyan-600 text-white',
    primaryHover: 'hover:bg-cyan-700',
    secondary: 'bg-teal-500 text-white',
    secondaryHover: 'hover:bg-teal-600',
    accent: 'bg-blue-500 text-white',
    accentHover: 'hover:bg-blue-600',
    border: 'border-cyan-200',
    borderSecondary: 'border-cyan-300',
    card: 'border-2 bg-white border-cyan-300',
    cardHover: 'hover:bg-cyan-50',
    input: 'bg-white border-cyan-300 text-cyan-900',
    shadow: 'shadow-lg',
    gradient: 'bg-gradient-to-r from-cyan-500 to-blue-500',
    // Button styling
    buttonPrimary: 'bg-cyan-600 text-white hover:bg-cyan-700',
    buttonSecondary: 'border border-cyan-600 text-cyan-600 hover:bg-cyan-50',
    buttonDanger: 'bg-pink-500 text-white hover:bg-pink-600',
    buttonGhost: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    // Tab styling
    tabActive: 'bg-cyan-600 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-cyan-600',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-cyan-600',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-cyan-600',
    tabInactive: 'text-cyan-700 hover:bg-cyan-100 hover:text-cyan-900',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-cyan-100',
      headerText: 'text-cyan-800',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-white',
      oddRowBg: 'bg-cyan-50',
      rowHover: 'hover:bg-cyan-100',
      border: 'border-cyan-200',
      textColor: 'text-cyan-900',
      textSecondary: 'text-cyan-700',
      winsColor: 'text-green-600',
      lossesColor: 'text-red-600',
      drawsColor: 'text-cyan-600'
    }
  },
  sunset: {
    name: 'Sunset',
    background: 'bg-orange-50',
    backgroundSecondary: 'bg-orange-100',
    text: 'text-orange-900',
    textSecondary: 'text-orange-700',
    textMuted: 'text-orange-600',
    link: 'text-orange-600',
    linkHover: 'hover:text-orange-800',
    primary: 'bg-orange-600 text-white',
    primaryHover: 'hover:bg-orange-700',
    secondary: 'bg-red-500 text-white',
    secondaryHover: 'hover:bg-red-600',
    accent: 'bg-yellow-500 text-orange-900',
    accentHover: 'hover:bg-yellow-400',
    border: 'border-orange-200',
    borderSecondary: 'border-orange-300',
    card: 'border-2 bg-white border-orange-300',
    cardHover: 'hover:bg-orange-50',
    input: 'bg-white border-orange-300 text-orange-900',
    shadow: 'shadow-lg',
    gradient: 'bg-gradient-to-r from-orange-500 to-red-500',
    // Button styling
    buttonPrimary: 'bg-orange-600 text-white hover:bg-orange-700',
    buttonSecondary: 'border border-orange-600 text-orange-600 hover:bg-orange-50',
    buttonDanger: 'bg-pink-500 text-white hover:bg-pink-600',
    buttonGhost: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    // Tab styling
    tabActive: 'bg-orange-500 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-orange-500',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-orange-500',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-orange-500',
    tabInactive: 'text-orange-700 hover:bg-orange-100 hover:text-orange-900',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-orange-100',
      headerText: 'text-orange-800',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-white',
      oddRowBg: 'bg-orange-50',
      rowHover: 'hover:bg-orange-100',
      border: 'border-orange-200',
      textColor: 'text-orange-900',
      textSecondary: 'text-orange-700',
      winsColor: 'text-green-600',
      lossesColor: 'text-red-600',
      drawsColor: 'text-orange-600'
    }
  },
  purple: {
    name: 'Purple',
    background: 'bg-purple-50',
    backgroundSecondary: 'bg-purple-100',
    text: 'text-purple-900',
    textSecondary: 'text-purple-700',
    textMuted: 'text-purple-600',
    link: 'text-purple-600',
    linkHover: 'hover:text-purple-800',
    primary: 'bg-purple-600 text-white',
    primaryHover: 'hover:bg-purple-700',
    secondary: 'bg-indigo-500 text-white',
    secondaryHover: 'hover:bg-indigo-600',
    accent: 'bg-pink-500 text-white',
    accentHover: 'hover:bg-pink-600',
    border: 'border-purple-200',
    borderSecondary: 'border-purple-300',
    card: 'border-2 bg-white border-purple-300',
    cardHover: 'hover:bg-purple-50',
    input: 'bg-white border-purple-300 text-purple-900',
    shadow: 'shadow-lg',
    gradient: 'bg-gradient-to-r from-purple-500 to-pink-500',
    // Button styling
    buttonPrimary: 'bg-purple-600 text-white hover:bg-purple-700',
    buttonSecondary: 'border border-purple-600 text-purple-600 hover:bg-purple-50',
    buttonDanger: 'bg-pink-500 text-white hover:bg-pink-600',
    buttonGhost: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    // Tab styling
    tabActive: 'bg-purple-600 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-purple-600',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-purple-600',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-purple-600',
    tabInactive: 'text-purple-700 hover:bg-purple-100 hover:text-purple-900',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-purple-100',
      headerText: 'text-purple-800',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-white',
      oddRowBg: 'bg-purple-50',
      rowHover: 'hover:bg-purple-100',
      border: 'border-purple-200',
      textColor: 'text-purple-900',
      textSecondary: 'text-purple-700',
      winsColor: 'text-green-600',
      lossesColor: 'text-red-600',
      drawsColor: 'text-purple-600'
    }
  },
  darkGreen: {
    name: 'Dark Green',
    background: 'bg-green-950',
    backgroundSecondary: 'bg-green-900',
    text: 'text-white',
    textSecondary: 'text-green-100',
    textMuted: 'text-green-200',
    link: 'text-green-400',
    linkHover: 'hover:text-green-300',
    primary: 'bg-green-600 text-white',
    primaryHover: 'hover:bg-green-700',
    secondary: 'bg-green-800 text-white',
    secondaryHover: 'hover:bg-green-700',
    accent: 'bg-emerald-500 text-white',
    accentHover: 'hover:bg-emerald-600',
    // One step lighter than `card`, deliberately. This was the card's own
    // background colour, so every bordered element sitting on a card had an
    // invisible border — buttons looked unbounded and checkboxes looked
    // absent until clicked. A border must differ from the surface it is on.
    border: 'border-green-700',
    borderSecondary: 'border-green-700',
    // One shade lighter than backgroundSecondary (bg-green-900) — see the
    // `dark` theme's `card` comment above for why.
    card: 'border-2 bg-green-800 border-green-600',
    cardHover: 'hover:bg-green-850',
    input: 'bg-green-900 border-green-700 text-white',
    shadow: 'shadow-xl',
    gradient: 'bg-gradient-to-r from-green-600 to-emerald-600',
    // Button styling
    buttonPrimary: 'bg-green-600 text-white hover:bg-green-700',
    buttonSecondary: 'border border-green-500 text-green-400 hover:bg-green-900',
    buttonDanger: 'bg-pink-600 text-white hover:bg-pink-700',
    buttonGhost: 'bg-gray-700 text-gray-200 hover:bg-gray-600',
    // Tab styling
    tabActive: 'bg-green-500 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-green-500',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-green-500',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-green-500',
    tabInactive: 'text-green-300 hover:bg-green-800 hover:text-green-100',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-green-800',
      headerText: 'text-green-100',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-green-950',
      oddRowBg: 'bg-green-900',
      rowHover: 'hover:bg-green-850',
      border: 'border-green-800',
      textColor: 'text-green-100',
      textSecondary: 'text-green-300',
      winsColor: 'text-green-400',
      lossesColor: 'text-red-400',
      drawsColor: 'text-green-300'
    }
  },
  darkBlue: {
    name: 'Dark Blue',
    background: 'bg-blue-950',
    backgroundSecondary: 'bg-blue-900',
    text: 'text-white',
    textSecondary: 'text-blue-100',
    textMuted: 'text-blue-200',
    link: 'text-blue-400',
    linkHover: 'hover:text-blue-300',
    primary: 'bg-blue-600 text-white',
    primaryHover: 'hover:bg-blue-700',
    secondary: 'bg-blue-800 text-white',
    secondaryHover: 'hover:bg-blue-700',
    accent: 'bg-sky-500 text-white',
    accentHover: 'hover:bg-sky-600',
    // One step lighter than `card`, deliberately. This was the card's own
    // background colour, so every bordered element sitting on a card had an
    // invisible border — buttons looked unbounded and checkboxes looked
    // absent until clicked. A border must differ from the surface it is on.
    border: 'border-blue-700',
    borderSecondary: 'border-blue-700',
    // One shade lighter than backgroundSecondary (bg-blue-900) — see the
    // `dark` theme's `card` comment above for why.
    card: 'border-2 bg-blue-800 border-blue-600',
    cardHover: 'hover:bg-blue-850',
    input: 'bg-white border-blue-700 text-gray-900',
    shadow: 'shadow-xl',
    gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600',
    // Button styling
    buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700',
    buttonSecondary: 'border border-blue-500 text-blue-400 hover:bg-blue-900',
    buttonDanger: 'bg-pink-600 text-white hover:bg-pink-700',
    buttonGhost: 'bg-gray-700 text-gray-200 hover:bg-gray-600',
    // Tab styling
    tabActive: 'bg-blue-500 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-blue-500',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-blue-500',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-blue-500',
    tabInactive: 'text-blue-300 hover:bg-blue-800 hover:text-blue-100',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-blue-800',
      headerText: 'text-blue-100',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-blue-950',
      oddRowBg: 'bg-blue-900',
      rowHover: 'hover:bg-blue-850',
      border: 'border-blue-800',
      textColor: 'text-blue-100',
      textSecondary: 'text-blue-300',
      winsColor: 'text-green-400',
      lossesColor: 'text-red-400',
      drawsColor: 'text-blue-300'
    }
  },
  darkPurple: {
    name: 'Dark Purple',
    background: 'bg-purple-950',
    backgroundSecondary: 'bg-purple-900',
    text: 'text-white',
    textSecondary: 'text-purple-100',
    textMuted: 'text-purple-200',
    link: 'text-purple-400',
    linkHover: 'hover:text-purple-300',
    primary: 'bg-purple-600 text-white',
    primaryHover: 'hover:bg-purple-700',
    secondary: 'bg-purple-800 text-white',
    secondaryHover: 'hover:bg-purple-700',
    accent: 'bg-fuchsia-500 text-white',
    accentHover: 'hover:bg-fuchsia-600',
    // One step lighter than `card`, deliberately. This was the card's own
    // background colour, so every bordered element sitting on a card had an
    // invisible border — buttons looked unbounded and checkboxes looked
    // absent until clicked. A border must differ from the surface it is on.
    border: 'border-purple-700',
    borderSecondary: 'border-purple-700',
    // One shade lighter than backgroundSecondary (bg-purple-900) — see the
    // `dark` theme's `card` comment above for why.
    card: 'border-2 bg-purple-800 border-purple-600',
    cardHover: 'hover:bg-purple-850',
    input: 'bg-purple-900 border-purple-700 text-white',
    shadow: 'shadow-xl',
    gradient: 'bg-gradient-to-r from-purple-600 to-pink-600',
    // Button styling
    buttonPrimary: 'bg-purple-600 text-white hover:bg-purple-700',
    buttonSecondary: 'border border-purple-500 text-purple-400 hover:bg-purple-900',
    buttonDanger: 'bg-pink-600 text-white hover:bg-pink-700',
    buttonGhost: 'bg-gray-700 text-gray-200 hover:bg-gray-600',
    // Tab styling
    tabActive: 'bg-purple-500 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-purple-500',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-purple-500',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-purple-500',
    tabInactive: 'text-purple-300 hover:bg-purple-800 hover:text-purple-100',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-purple-800',
      headerText: 'text-purple-100',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-purple-950',
      oddRowBg: 'bg-purple-900',
      rowHover: 'hover:bg-purple-850',
      border: 'border-purple-800',
      textColor: 'text-purple-100',
      textSecondary: 'text-purple-300',
      winsColor: 'text-green-400',
      lossesColor: 'text-red-400',
      drawsColor: 'text-purple-300'
    }
  },
  darkOrange: {
    name: 'Dark Orange',
    background: 'bg-orange-950',
    backgroundSecondary: 'bg-orange-900',
    text: 'text-white',
    textSecondary: 'text-orange-100',
    textMuted: 'text-orange-200',
    link: 'text-orange-400',
    linkHover: 'hover:text-orange-300',
    primary: 'bg-orange-600 text-white',
    primaryHover: 'hover:bg-orange-700',
    secondary: 'bg-orange-800 text-white',
    secondaryHover: 'hover:bg-orange-700',
    accent: 'bg-amber-500 text-white',
    accentHover: 'hover:bg-amber-600',
    // One step lighter than `card`, deliberately. This was the card's own
    // background colour, so every bordered element sitting on a card had an
    // invisible border — buttons looked unbounded and checkboxes looked
    // absent until clicked. A border must differ from the surface it is on.
    border: 'border-orange-700',
    borderSecondary: 'border-orange-700',
    // One shade lighter than backgroundSecondary (bg-orange-900) — see the
    // `dark` theme's `card` comment above for why.
    card: 'border-2 bg-orange-800 border-orange-600',
    cardHover: 'hover:bg-orange-850',
    input: 'bg-orange-900 border-orange-700 text-white',
    shadow: 'shadow-xl',
    gradient: 'bg-gradient-to-r from-orange-600 to-red-600',
    // Button styling
    buttonPrimary: 'bg-orange-600 text-white hover:bg-orange-700',
    buttonSecondary: 'border border-orange-500 text-orange-400 hover:bg-orange-900',
    buttonDanger: 'bg-pink-600 text-white hover:bg-pink-700',
    buttonGhost: 'bg-gray-700 text-gray-200 hover:bg-gray-600',
    // Tab styling
    tabActive: 'bg-orange-500 text-white shadow-sm',
    /* The 'on' fill for a switch built as a <button role="switch"> rather
       than a peer-checked input — a bare background, no variant. */
    switchOn: 'bg-orange-500',
    /* An accent border — the ring around whatever is current. Same family as
       tabActive, so a highlighted edge follows the academy's colour. */
    borderAccent: 'border-orange-500',
    /* The 'on' colour for a switch. Variant included: Tailwind needs
       peer-checked: on the class itself, so a caller cannot prefix a token. */
    toggleOn: 'peer-checked:bg-orange-500',
    tabInactive: 'text-orange-300 hover:bg-orange-800 hover:text-orange-100',
    tabBase: 'px-4 py-2 rounded-lg font-medium text-sm transition-colors flex-shrink-0',
    // Table styling
    table: {
      headerBg: 'bg-orange-800',
      headerText: 'text-orange-100',
      headerTextStyle: 'font-bold uppercase tracking-wide',
      evenRowBg: 'bg-orange-950',
      oddRowBg: 'bg-orange-900',
      rowHover: 'hover:bg-orange-850',
      border: 'border-orange-800',
      textColor: 'text-orange-100',
      textSecondary: 'text-orange-300',
      winsColor: 'text-green-400',
      lossesColor: 'text-red-400',
      drawsColor: 'text-orange-300'
    }
  }
};

/**
 * The dot beside a theme's name.
 *
 * Themes are Tailwind class names, so nothing in them is a colour a swatch can
 * be painted with. The header carried this as an inline ternary chain; it is
 * here now because the academy theme picker needs the same answer, and two
 * copies would drift the first time a theme is added.
 */
export const THEME_SWATCH = {
  light:      '#3b82f6',
  dark:       '#1f2937',
  sports:     '#16a34a',
  ocean:      '#0891b2',
  sunset:     '#ea580c',
  purple:     '#9333ea',
  darkGreen:  '#059669',
  darkBlue:   '#2563eb',
  darkPurple: '#9333ea',
  darkOrange: '#ea580c',
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('darkBlue');

  useEffect(() => {
    const savedTheme = localStorage.getItem('exsports-theme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('exsports-theme', themeName);
    }
  };

  const value = {
    currentTheme,
    theme: themes[currentTheme],
    isDark: DARK_THEMES.has(currentTheme),
    themes: Object.keys(themes).map(key => ({ key, ...themes[key] })),
    changeTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * An academy's theme, for the academy's screens only.
 *
 * A nested provider rather than a flag on the global one, because scope is the
 * whole requirement: Kynaix Sports keeps its own look — default Dark Blue, or
 * whatever the visitor chose in profile → Themes — and only what this wraps
 * renders in the academy's. The first version set it globally and it leaked
 * onto the home page, which is exactly the bug this shape cannot have.
 *
 * Nothing is written to localStorage. The visitor's preference is untouched and
 * returns by itself the moment they navigate out, because this provider simply
 * stops being in the tree.
 *
 * `previewAcademyTheme` lets Design Studio — which renders inside this scope —
 * show a theme before it is saved, without telling anybody else about it.
 */
/**
 * The academy theme currently on screen, published for the few things that
 * render OUTSIDE the academy's subtree and still belong to it.
 *
 * ⚠️ THE STEP-UP MODAL IS WHY THIS EXISTS. <StepUpGate> is mounted once at the
 * app root — it has to be, since any screen can demand a code — so it sits
 * above AcademyThemeScope in the tree and can never see the academy's theme
 * through context. It rendered in Kynaix blue on top of an orange academy while
 * confirming an action that belonged to that academy.
 *
 * A module-level store rather than more context, because the reader is a
 * SIBLING of the provider, not a descendant: no arrangement of providers puts
 * the root modal inside a scope that is mounted below it.
 */
let scopedAcademyKey = null;
const scopedListeners = new Set();

const publishScopedAcademyTheme = (key) => {
  if (key === scopedAcademyKey) return;
  scopedAcademyKey = key;
  scopedListeners.forEach(fn => fn());
};

/** The academy theme key in view, or null when the visitor is not inside one. */
export const useScopedAcademyTheme = () => useSyncExternalStore(
  (onChange) => { scopedListeners.add(onChange); return () => scopedListeners.delete(onChange); },
  () => scopedAcademyKey,
  () => null,
);

export const AcademyThemeScope = ({ themeKey, children }) => {
  const parent = useTheme();
  const [preview, setPreview] = useState(null);

  // A different academy is a different theme; a stale preview must not follow.
  useEffect(() => { setPreview(null); }, [themeKey]);

  const key = (preview && themes[preview]) ? preview
            : (themeKey && themes[themeKey]) ? themeKey
            : null;

  // Published for the app root. Cleared on the way out, so navigating off an
  // academy takes its colour with it.
  useEffect(() => {
    publishScopedAcademyTheme(key);
    return () => publishScopedAcademyTheme(null);
  }, [key]);

  const value = useMemo(() => ({
    ...parent,
    previewAcademyTheme: setPreview,
    ...(key ? {
      currentTheme: key,
      theme: themes[key],
      isDark: DARK_THEMES.has(key),
      // What the visitor picked for themselves, so the profile menu still ticks
      // their theme rather than the academy's.
      userTheme: parent.currentTheme,
    } : { userTheme: parent.currentTheme }),
  }), [parent, key]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
